import { cliente } from '@/lib/cliente';
import type { IngestaBlendRequest, IngestaBlendResponse, RespuestaApi } from '@adelante/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

/**
 * Mutation para POST /api/batches/ingestar.
 *
 * - `onSuccess`: muestra toast según `estado` y invalida KPIs + historial.
 * - `onError`: muestra `toast.error` con el mensaje del backend.
 */
export function useIngestarBatch() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<IngestaBlendResponse>, Error, IngestaBlendRequest>({
    mutationFn: (body) => cliente.batches.ingestar(body),
    onSuccess: (respuesta) => {
      const data = respuesta.data;
      if (!data) {
        toast.error(respuesta.error?.mensaje ?? 'Error desconocido en ingesta');
        return;
      }

      const insertados = data.resumen.batches_insertados;
      const dups = data.resumen.batches_omitidos_duplicado;
      switch (data.estado) {
        case 'ok': {
          // Mensaje según insertados/duplicados (todos los casos son "OK"):
          //   - insertados > 0 y sin dups → "X batches subidos"
          //   - insertados > 0 y con dups → "X batches subidos (Y duplicados)"
          //   - insertados = 0 y dups > 0 → "Sin batches nuevos (Y duplicados)"
          //   - insertados = 0 y dups = 0 → caso raro pero posible (CSV vacío parseable)
          let msg: string;
          let desc: string | undefined;
          if (insertados > 0 && dups === 0) {
            msg = `${insertados} ${insertados === 1 ? 'batch subido' : 'batches subidos'} exitosamente`;
          } else if (insertados > 0 && dups > 0) {
            msg = `${insertados} ${insertados === 1 ? 'batch subido' : 'batches subidos'} exitosamente`;
            desc = `${dups} ${dups === 1 ? 'fila omitida' : 'filas omitidas'} por estar ya en BD.`;
          } else if (dups > 0) {
            msg = 'Archivo ya conocido — sin batches nuevos';
            desc = `Los ${dups} batches del archivo ya estaban en BD.`;
          } else {
            msg = 'CSV procesado, sin batches insertados';
          }
          toast.success(msg, desc ? { description: desc } : undefined);
          break;
        }
        case 'duplicado_archivo':
          toast.warning('Archivo ya procesado anteriormente', {
            description: `Importación previa #${data.id_importacion}, sin cambios.`,
          });
          break;
        case 'parcial':
          toast.warning(`Ingesta parcial: ${insertados} insertados`, {
            description: `${data.errores.length} fila(s) con error${
              dups > 0 ? `, ${dups} omitida(s) por duplicado` : ''
            }.`,
          });
          break;
      }

      qc.invalidateQueries({ queryKey: ['kpis'] });
      qc.invalidateQueries({ queryKey: ['historial-importaciones'] });
    },
    onError: (e) => {
      toast.error('Error al subir CSV', { description: e.message });
    },
  });
}
